// Analytics dispatch — push to dataLayer AND POST to sGTM directly.
//
// The dataLayer push is the long-term production path (consumed by
// CC's existing Web Container with "Send to server container" enabled
// on booking-related tags).
//
// The direct sGTM POST is a temporary transport so we can validate the
// end-to-end flow against the 2x4 test GA4 property today, before the
// Web Container has been wired. Once Web Container forwarding is live,
// this transport can be removed.

import type { EventParams } from './events';
import { ONCE_PER_SESSION, SCHEMA_VERSION } from './events';
import { maybeEmitLegacy } from './legacy-dispatch';

let clientId: string | null = null;
let sessionId: string | null = null;
let webSubmissionId: string | null = null;
let persistentUserId: string | null = null;
const firedThisSession = new Set<string>();

/**
 * Stable session-scoped client identifier in the GA4 cookie format.
 * No actual cookie — derived in-memory only.
 */
function getClientId(): string {
  if (clientId) return clientId;
  const rand = Math.floor(Math.random() * 1e10);
  clientId = `${rand}.${Math.floor(Date.now() / 1000)}`;
  return clientId;
}

function getSessionId(): string {
  if (sessionId) return sessionId;
  sessionId = String(Math.floor(Date.now() / 1000));
  return sessionId;
}

/**
 * Per-session widget submission ID. Format: `WS-<random>`.
 * Generated once on first emit, reused for the rest of the session.
 * CC prepends "WS-" when storing in Salesforce per Dan's May 29 contract;
 * we generate the same prefix here so the value is portable end-to-end.
 */
function getWebSubmissionId(): string {
  if (webSubmissionId) return webSubmissionId;
  const rand = Math.random().toString(36).slice(2, 12).toUpperCase();
  webSubmissionId = `WS-${rand}`;
  return webSubmissionId;
}

/**
 * Set the persistent user_id (= Salesforce Account ID) once we have it
 * from the booking response. Subsequent events automatically include it.
 */
export function setAnalyticsUserId(id: string): void {
  if (id) persistentUserId = id;
}

/**
 * Read the ChannelMix client ID from the host page, if present. The
 * existing CC site uses a {{ChannelMix Client ID}} dataLayer variable;
 * on the staging host we don't have ChannelMix loaded, so this returns
 * undefined and the field is omitted.
 */
function getChannelMixClientId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const fromConfig = (window.CC_WIDGET_CONFIG ?? {}).channelmixClientId;
  if (typeof fromConfig === 'string' && fromConfig.length > 0) return fromConfig;
  const fromGlobal = (window as unknown as { channelmix_client_id?: string }).channelmix_client_id;
  if (typeof fromGlobal === 'string' && fromGlobal.length > 0) return fromGlobal;
  return undefined;
}

/**
 * Push to window.dataLayer. CC's Web Container picks up these events
 * once Michael wires the triggers.
 */
function pushToDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * Direct POST to sGTM. Mirrors the shape of a GA4 collect request so the
 * GA4 Client in the server container parses it without a custom template.
 *
 * Exported so the legacy-dispatch sidecar can use the same transport
 * (sharing client/session IDs and config) when emitting the legacy
 * `booking` schema alongside the new schema.
 *
 * Fire and forget — analytics failures must never block the booking flow.
 */
export function postToSgtm(event: string, params: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  const cfg = window.CC_WIDGET_CONFIG ?? {};
  const sgtmUrl = cfg.sgtmUrl;
  const measurementId = cfg.ga4MeasurementId;
  if (!sgtmUrl || !measurementId) return;

  const cid = getClientId();
  const sid = getSessionId();
  const query = new URLSearchParams({
    v: '2',
    tid: measurementId,
    cid,
    _p: String(Date.now()),
    en: event,
    dl: window.location.href,
    dt: document.title,
    _et: '100',
    sid,
    seg: '1',
    // Staging-only: flag events as debug so they appear in DebugView.
    _dbg: '1',
  });

  // Top-level fields (NOT under ep.) per GA4 Measurement Protocol.
  // user_id → uid, channelmix_client_id → up.channelmix_client_id (user-scoped)
  const topLevelMap: Record<string, string> = {
    user_id: 'uid',
  };
  const userPropertyKeys = new Set(['channelmix_client_id']);

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (k in topLevelMap && topLevelMap[k]) {
      query.set(topLevelMap[k] as string, String(v));
      continue;
    }
    if (userPropertyKeys.has(k)) {
      query.set(`up.${k}`, String(v));
      continue;
    }
    if (Array.isArray(v) || typeof v === 'object') {
      query.set(`ep.${k}`, JSON.stringify(v));
    } else {
      query.set(`ep.${k}`, String(v));
    }
  }

  // GA4 collect: the params live in the URL, sGTM's GA4 client parses them
  // off the query string. We POST a synthetic body so the beacon survives
  // the page-unload window even when the browser refuses query-string-only
  // beacons under memory pressure.
  const url = `${sgtmUrl.replace(/\/$/, '')}/g/collect?${query.toString()}`;
  const body = new Blob([''], { type: 'text/plain' });

  // navigator.sendBeacon can return false (queue full, payload too large,
  // quota exhausted) — modern Chrome and Safari throttle aggressively when
  // many beacons fire in quick succession, which happens at the conversion
  // moment because book_flow_complete dual-fires with legacy step=5 and is
  // immediately followed by channelmix_conversion. When that throttle hits
  // we lose the event silently unless we fall through to fetch+keepalive,
  // which has a separate queue managed by the network stack.
  let queued = false;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      queued = navigator.sendBeacon(url, body);
    }
  } catch {
    queued = false;
  }
  if (!queued) {
    try {
      void fetch(url, {
        method: 'POST',
        body,
        keepalive: true,
        credentials: 'omit',
        // Avoid CORS preflight: text/plain is a safelisted Content-Type
        headers: { 'Content-Type': 'text/plain' },
      });
    } catch {
      // Swallow — analytics never blocks UX.
    }
  }
}

/**
 * Public emit function. Auto-injects schema_version, user_id (if known),
 * channelmix_client_id (if present), and the per-session web_submission_id.
 *
 * Honors the ONCE_PER_SESSION list — events in that set fire at most once
 * per page load.
 */
export function emitAnalytics(payload: EventParams): void {
  const eventName = payload.event;

  if (ONCE_PER_SESSION.has(eventName) && firedThisSession.has(eventName)) {
    return;
  }
  firedThisSession.add(eventName);

  // Spread caller params, then layer auto-injected fields. Caller can
  // override auto-injected values if they explicitly pass them.
  const enriched: Record<string, unknown> = {
    schema_version: SCHEMA_VERSION,
    web_submission_id: getWebSubmissionId(),
    channelmix_client_id: getChannelMixClientId(),
    user_id: persistentUserId ?? undefined,
    ...(payload as unknown as Record<string, unknown>),
  };

  // dataLayer push uses the standard GTM `event` key. We strip our
  // typed `event` discriminant when posting to sGTM since it's used as
  // the GA4 event name in `en=...`.
  pushToDataLayer({ ...enriched });

  // For sGTM, the `event` key on the payload becomes the GA4 event name
  // (already used as `en=` query param). Don't send it twice.
  const { event: _event, ...sgtmParams } = enriched as Record<string, unknown>;
  postToSgtm(eventName, sgtmParams);

  // Optional legacy `booking` schema dual-fire, gated on host-page config.
  // No-op unless window.CC_WIDGET_CONFIG.legacyDualFire is true.
  maybeEmitLegacy(payload);
}
